interface RootErrorScreenProps {
  error: unknown;
  onReset: () => void;
}

/**
 * Top-level fallback rendered when an uncaught render error escapes every
 * inner ErrorBoundary. Mirrors MaintenanceScreen styling so the brand stays
 * consistent on the worst-case path. Production users see generic copy; in
 * dev the error message + stack are surfaced to make the bug actionable.
 */
export function RootErrorScreen({ error, onReset }: RootErrorScreenProps) {
  const isDev =
    typeof import.meta !== 'undefined' && Boolean((import.meta as any).env?.DEV);

  const message =
    error instanceof Error ? error.message : typeof error === 'string' ? error : '';
  const stack = error instanceof Error ? error.stack : undefined;

  const reload = () => window.location.reload();

  return (
    <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-background px-6 text-center overflow-auto">
      <div className="mb-8">
        <svg
          className="w-20 h-20 mx-auto"
          viewBox="0 0 64 64"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <circle cx="32" cy="32" r="28" className="fill-destructive/15" />
          <circle cx="32" cy="32" r="20" className="fill-destructive/30" />
          <path
            d="M32 22V34"
            className="stroke-destructive"
            strokeWidth="3"
            strokeLinecap="round"
          />
          <circle cx="32" cy="42" r="2" className="fill-destructive" />
        </svg>
      </div>

      <h1 className="text-2xl font-display font-bold text-foreground mb-3">
        Er ging iets mis
      </h1>
      <p className="text-muted-foreground max-w-sm leading-relaxed">
        Clando liep tegen een onverwachte fout aan.
        <br />
        Probeer het opnieuw — je gegevens blijven veilig.
      </p>

      {isDev && message && (
        <div className="mt-6 max-w-2xl w-full rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-left">
          <p className="text-xs font-semibold text-destructive mb-2">
            Dev-only error detail
          </p>
          <p className="text-xs font-mono text-foreground/90 break-words">
            {message}
          </p>
          {stack && (
            <pre className="text-[11px] font-mono text-muted-foreground mt-2 max-h-64 overflow-auto whitespace-pre-wrap">
              {stack}
            </pre>
          )}
        </div>
      )}

      <div className="mt-8 flex gap-3">
        <button
          onClick={onReset}
          className="px-5 py-2.5 rounded-lg border border-border bg-background text-foreground font-medium text-sm hover:bg-muted transition-colors"
        >
          Opnieuw proberen
        </button>
        <button
          onClick={reload}
          className="px-5 py-2.5 rounded-lg bg-primary text-primary-foreground font-medium text-sm hover:bg-primary/90 transition-colors"
        >
          App herladen
        </button>
      </div>
    </div>
  );
}
